import React, { useState, useRef, useEffect, useCallback, useContext, useMemo } from 'react';
import ReactPlayer from 'react-player';
import { MultipleVideoPlayerProps } from '../types/data';
import { styled } from 'styled-components';
import { theme } from '../styles/theme';
import PlayerControls from './PlayerControls';
import { ProgressContext } from 'src/ProgressContext';
const LazyReactPlayer = React.lazy(() => import('react-player'));

const StyledPlayerWrapper = styled.div<{size?: string}>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width:100%;
  height: 100%;
  border-radius: 100%;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
  
`;

const VideoCounter = styled.div`
  // width: 50px;
  height: 26px;
  border-radius: 100px;
  padding: 8px;
  background: #190F494D;
  backdrop-filter: blur(10px);
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;

  font-family: ${theme.fonts.primary};
  font-weight: ${theme.fontWeights.medium};
  font-size: ${theme.fontSizes.md};
  leading-trim: Cap height;
  line-height: 100%;
  letter-spacing: 0%;
  
  position: absolute;
  bottom: 30px;

  color: #fff;

  @media (max-width: ${theme.breakpoints.desktop}px) {
    font-size: calc(${theme.fontSizes.md} * (${theme.breakpoints.desktop} / 1920));
  }

  @media (max-width: ${theme.breakpoints.mobile}px) {
    font-size: calc(${theme.fontSizes.md} * (${theme.breakpoints.mobile} / 1920));
  }

  @media (max-width: ${theme.breakpoints.tablet}px) {
    font-size: calc(${theme.fontSizes.md} * (${theme.breakpoints.tablet} / 1920));
  }

  @media (max-width: ${theme.breakpoints.maxTablet}px) {
    font-size: calc(${theme.fontSizes.md} * (${theme.breakpoints.maxTablet} / 1920));
  }

  @media (max-width: ${theme.breakpoints.maxDesktop}px) {
    font-size: calc(${theme.fontSizes.md} * (${theme.breakpoints.maxDesktop} / 1920));
  }
`;

const MultipleVideoPlayer: React.FC<MultipleVideoPlayerProps> = ({
  videos, 
  currentIndex = 0,
  overidePlay=false,
  size = "520px",
  repeatVideoPlayback,
  playing,
  muteVideo,
  onVideoEnded,
  onVideoCompleted,
  onProgress,
  onDuration,
  onPause,
  onSeek,
  onNextVideo,
  seekVideo={status:false}
}) => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [played, setPlayed] = useState(0);
  const [repeat, setRepeat] = useState(false);
  const [totalDuration, setTotalDuration] = useState(0);
  const [videoDurations, setVideoDurations] = useState<number[]>(Array(videos.length).fill(0));
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
   const { setProgressTracker } = useContext(ProgressContext);
 
  
  const playerRefs = useRef<(ReactPlayer | null)[]>([]);
  
  useEffect(() => {
    playerRefs.current = Array(videos.length).fill(null);
  }, [videos.length]);

  
  useEffect(() => {
    const total = videoDurations.reduce((sum, duration) => sum + duration, 0);
    setTotalDuration(total);
  }, [videoDurations]);

  useEffect(()=>{
    if(currentIndex > -1){
      setCurrentVideoIndex(currentIndex);
    }
  }, [currentIndex]);

  useEffect(()=>{
    if(repeatVideoPlayback){
      setRepeat(true);
    }

  }, [repeatVideoPlayback]);

  useEffect(()=>{
    onDuration(totalDuration);
  }, [totalDuration]);
  useEffect(()=>{
    if (seekVideo.status) {
      const player = playerRefs.current[currentVideoIndex];
      if (!player.getInternalPlayer().paused) {
        player.seekTo(seekVideo.to || 0);
      } else {
        player.getInternalPlayer().play().then(() => {
          player.seekTo(seekVideo.to || 0);
          player.getInternalPlayer().pause();
          
        });
      }
    }
      
   
    
  }, [seekVideo]);
  
  useEffect(()=>{
    if(playing){
      setIsPlaying(true);
    }else{
      setIsPlaying(false);
    }
  }, [playing]);

  useEffect(()=>{
    setIsMuted(muteVideo);
  }, [muteVideo]);

  
  
  const handleDuration = (duration: number, index: number) => {
    setVideoDurations(prev => {
      const newDurations = [...prev];
      newDurations[index] = duration;
      return newDurations;
    });
  };

  const handleProgress = useCallback(({ playedSeconds }) => {
    setPlayed(playedSeconds);
    onProgress(playedSeconds);
  }, []);

  
  const sumPreviousValues=(arr, currentIndex)=> {
    if (currentIndex <= 0) {
      return 0;
    }
    
    
    let sum = 0;
    for (let i = 0; i < currentIndex; i++) {
      sum += arr[i].duration;
    }
    
    return sum;
  }
  useEffect(()=>{
    setProgressTracker(sumPreviousValues(videos,currentIndex)+played)
  }, [played,currentVideoIndex,videos]);

 
  
  const handleEnded = () => {
    if (currentVideoIndex < videos.length - 1) {
      onVideoEnded();
      handleNextVideo();
    } else {
      if(onVideoCompleted){
        onVideoCompleted()
      }
      setIsPlaying(false);
    }
  };

  
  const handlePrevVideo = () => {
    if (currentVideoIndex > 0) {
      const current: number = currentVideoIndex - 1;
      setCurrentVideoIndex(current);
      onNextVideo(current);
    }
  };

  
  const handleNextVideo = () => {
    if (currentVideoIndex < videos.length - 1) {
      const current: number = currentVideoIndex + 1;
      setCurrentVideoIndex(current);
      onNextVideo(current);
    }

    if(currentVideoIndex >= videos.length - 1 && repeat){
      setCurrentVideoIndex(0);
      onNextVideo(0);
    }
  };

  const handlePlayPause = useCallback((status: boolean) => {
    if(!overidePlay){
      setIsPlaying(status);
    }
    
    onPause(status);
  }, [onPause]);

  
  const setPlayerRef = (index: number) => (player: ReactPlayer | null) => {
    playerRefs.current[index] = player;
  };


  const videoUrl = useMemo(() => {
    if (!videos || currentVideoIndex < 0 || currentVideoIndex >= videos.length) {
      return '';
    }

    return videos[currentVideoIndex].url;
  }, [currentVideoIndex, videos]);


  return (
    <StyledPlayerWrapper size={size}>
      <VideoCounter>{currentVideoIndex + 1} / {videos.length}</VideoCounter>
      {videos.length>0 && (
        <LazyReactPlayer
          ref={setPlayerRef(currentVideoIndex)}
          url={videoUrl}
          playing={isPlaying}
          muted={isMuted}
          pip={false}
          controls={false}
          width="100%"
          height="100%"
          preload="auto"
          progressInterval={100}
          onDuration={(duration) => handleDuration(duration, currentVideoIndex)}
          onProgress={handleProgress}
          onEnded={handleEnded}
          className="circular-react-player"
          config={{ file: { attributes: { playsInline: true, autoplay:isPlaying, muted:isMuted, preload:"auto" }, }, }}
          
        />
      )}
      <PlayerControls 
          handlePlayPause={handlePlayPause} 
          playing={isPlaying} 
          handleSeekChange={()=>{}} 
          hideSeek={true}
        />
    </StyledPlayerWrapper>
  );
};

export default MultipleVideoPlayer;